import React from 'react';
import { Link, useLocation } from 'react-router-dom';

// Topic-matched conversion block for content pages. Compliance, payments,
// health, and blockchain pages route to a consultation (prefilled quote form);
// CRM/data-cleanup pages route to the free CRM signup.
const TOPICS = [
  {
    words: ['crm', 'helpdesk', 'csv-deduplicator', 'email-validator', 'phone-formatter', 'vcard-generator', 'introducing-og-crm'],
    kind: 'trial',
    eyebrow: 'OG CRM & Ticketing',
    heading: 'Put clean contacts, deals, and support tickets on one record',
    body: 'Import your cleaned CSV straight into OG CRM. Contacts, pipeline, activities, and a built-in helpdesk. The free plan is a real plan; Pro is EUR 8/user/month with a 14-day trial, no credit card required.',
    primary: { label: 'Start free — no credit card', to: '/signup/' },
    secondary: { label: 'Compare plans', to: '/pricing/' },
  },
  {
    words: ['iso-27001', 'soc-2', 'nis2', 'cyber-resilience', 'security-tools', 'iam-policy', 'aws-iam', 'aws-arn', 'hash-generator'],
    eyebrow: 'Information security compliance',
    heading: 'Turn your gap list into an audit-ready ISMS',
    body: 'Our consultants scope your ISMS, write the Statement of Applicability, and close Annex A gaps for ISO 27001, SOC 2, and NIS2, so you reach Stage 1 with evidence, not a to-do list.',
    service: 'ISO 27001 / SOC 2 / NIS2 compliance',
    primary: { label: 'Book a free 30-minute ISMS review' },
    secondary: { label: 'Run the free gap analysis', to: '/tools/iso-27001-gap-analysis/' },
  },
  {
    words: ['iso-42001', 'eu-ai-act', 'en-18286', 'iso-9001', 'integrated-management'],
    eyebrow: 'AI governance & quality management',
    heading: 'Get ready for ISO 42001 and the EU AI Act',
    body: 'We build AI management systems that satisfy ISO/IEC 42001, EN 18286, and the AI Act quality management requirements, integrated with your existing ISO 9001 or 27001 system.',
    service: 'ISO 42001 / EU AI Act compliance',
    primary: { label: 'Book a free 30-minute AI governance review' },
    secondary: { label: 'Free ISO 42001 readiness check', to: '/tools/iso-42001-ai-readiness/' },
  },
  {
    words: ['iban', 'bic', 'lei', 'sepa', 'mt940', 'camt', 'iso-20022', 'stellar', 'digital-payments', 'payment'],
    eyebrow: 'Payment infrastructure',
    heading: 'Migrating from MT940 or building on ISO 20022?',
    body: 'We design and implement ISO 20022 payment flows: camt.053 statement migration, SEPA pain.001/pain.008 generation, and bank and Stellar anchor integrations.',
    service: 'Payment infrastructure / ISO 20022 migration',
    primary: { label: 'Book a free 30-minute payments review' },
    secondary: { label: 'See payment services', to: '/products/payment-infrastructure/' },
  },
  {
    words: ['hl7', 'fhir'],
    eyebrow: 'Health IT integration',
    heading: 'Need HL7 v2 or FHIR integration in production?',
    body: 'We build HL7 v2 interfaces, FHIR R4 APIs, and v2-to-FHIR mappings for hospitals, labs, and health-tech products, with PHI handled to GDPR standards.',
    service: 'Health IT integration (HL7 / FHIR)',
    primary: { label: 'Book a free 30-minute integration review' },
    secondary: { label: 'See health IT services', to: '/products/healthcare-solutions/' },
  },
  {
    words: ['scorm', 'xapi', 'elearning'],
    eyebrow: 'E-learning platforms',
    heading: 'Building or migrating an LMS?',
    body: 'We develop SCORM and xAPI-compliant learning platforms and content pipelines, from LRS integration to course packaging.',
    service: 'E-learning platform development',
    primary: { label: 'Book a free 30-minute LMS review' },
    secondary: { label: 'See e-learning services', to: '/products/elearning-platform/' },
  },
  {
    words: ['ethereum', 'wei', 'keccak', 'function-selector', 'event-topic', 'abi', 'address-checksum', 'blockchain', 'dora', 'tokeniz', 'o1js', 'ebsi', 'eidas', 'web3', 'meridian'],
    eyebrow: 'Blockchain engineering',
    heading: 'Shipping smart contracts or tokenized assets?',
    body: 'We design, build, and review Ethereum and Stellar systems, from contract architecture and ABI integration to MiCA and DORA compliance for crypto-asset service providers.',
    service: 'Blockchain consulting / smart contract development',
    primary: { label: 'Book a free 30-minute architecture review' },
    secondary: { label: 'See blockchain services', to: '/products/blockchain-consulting/' },
  },
  {
    words: ['claude', 'gpt', 'devin', 'deepseek', 'gemini', 'kimi', 'glm'],
    eyebrow: 'AI agents',
    heading: 'Put AI agents to work on your workflows',
    body: 'We build production AI agents and chatbots with MCP tool integrations, guardrails, and evaluation, choosing the right model tier for your cost and latency budget.',
    service: 'AI agent & chatbot development',
    primary: { label: 'Book a free 30-minute AI agent review' },
    secondary: { label: 'See AI services', to: '/products/ai-chatbot/' },
  },
].map((t) => ({ ...t, re: new RegExp(`(^|[/-])(${t.words.join('|')})`) }));

const DEFAULT_TOPIC = {
  eyebrow: 'Need more than a free tool?',
  heading: 'Get expert help from the team behind these tools',
  body: 'OG Technologies EU is a Vienna-based IT consultancy for compliance, payments, health IT, and blockchain. Tell us what you are building and get 30 minutes with a specialist, free.',
  service: '',
  primary: { label: 'Book a free 30-minute review' },
  secondary: { label: 'Browse services', to: '/products/' },
};

const CONTENT_SECTIONS = /^\/(tools|insights|glossary|blog)\/./;

export function ContextualCta() {
  const { pathname } = useLocation();
  if (!CONTENT_SECTIONS.test(pathname)) return null;

  const topic = TOPICS.find((t) => t.re.test(pathname)) || DEFAULT_TOPIC;
  const from = pathname.replace(/\/?$/, '/');
  const primaryTo = topic.kind === 'trial'
    ? topic.primary.to
    : `/quote/?${new URLSearchParams({ offer: 'review', ...(topic.service ? { service: topic.service } : {}), from })}`;
  const showSecondary = topic.secondary && topic.secondary.to !== from;

  return (
    <aside aria-label={topic.eyebrow} className="max-w-6xl mx-auto px-4 sm:px-6 mb-8 md:mb-12">
      <div className="relative rounded-xl border border-purple-700/60 bg-gradient-to-r from-purple-900/40 to-gray-900 px-6 py-8 md:px-10 md:py-10 md:flex md:items-center md:justify-between gap-8">
        <div className="md:max-w-2xl">
          <p className="text-sm font-semibold uppercase tracking-wider text-purple-400 mb-2">{topic.eyebrow}</p>
          <h2 className="h4 text-white mb-3">{topic.heading}</h2>
          <p className="text-gray-400">{topic.body}</p>
        </div>
        <div className="mt-6 md:mt-0 flex flex-col sm:flex-row md:flex-col gap-3 shrink-0">
          <Link
            to={primaryTo}
            data-cta={topic.kind === 'trial' ? 'crm-trial' : 'consult'}
            className="btn text-white bg-purple-600 hover:bg-purple-700 text-center"
          >
            {topic.primary.label}
          </Link>
          {topic.kind !== 'trial' && (
            <p className="text-xs text-gray-500 text-center">Free · 30 minutes · no obligation</p>
          )}
          {showSecondary && (
            <Link to={topic.secondary.to} className="btn text-gray-200 bg-gray-800 hover:bg-gray-700 whitespace-nowrap">
              {topic.secondary.label}
            </Link>
          )}
        </div>
      </div>
    </aside>
  );
}
